import { StatusTypes } from '@components/common/StatusBadge/StatusBadge.component'
import { StatusType } from '@constants/Marketing'
import { ProviderDetails } from '@dataTypes/appointment.type'
import { IContact } from './contact.type'

export interface IAnalyticsData {
  previous: number
  current: number
  percentage: number
  status: StatusType
}

export enum marketingOverviewFilter {
  '1D' = '1D',
  '1W' = '1W',
  '1M' = '1M',
  '1Y' = '1Y',
  'YTD' = 'YTD',
}

export enum CampaignImageType {
  logo = 'Logo',
  header = 'Header',
  layout1 = 'Layout1',
  layout2 = 'Layout2',
  layout3 = 'Layout3',
  icon = 'Icon',
  default = 'Default',
}

export enum CampaignStatus {
  draft = 'Draft',
  active = 'Active',
  pending = 'Pending',
  completed = 'Completed',
}

export enum CampaignTemplateType {
  predefined = 'Predefined',
  saved = 'Saved',
}

export enum CampaignChannel {
  email = 'Email',
  instagram = 'Instagram',
  facebook = 'Facebook',
  twitter = 'Twitter',
  tiktok = 'TikTok',
  linkedin = 'LinkedIn',
  sms = 'SMS',
}

export interface CampaignChannelResponse {
  campaign_channel_id: string
  name: string
  sent_count: number
  open_count: number
  click_count: number
}

export interface ICampaignChannel {
  campaign_channel_id?: string
  name: CampaignChannel
  sent_count: number
  open_count: number
  click_count: number
  campaign_id: string
  is_hidden: boolean
}

export interface ICampaignImage {
  campaign_image_id?: string
  name: string
  url: string
  image_type: CampaignImageType
}

export interface CampaignImageResponse {
  campaign_image_id: string
  url: string
  image_type: CampaignImageType
}

export interface CampaignTagResponse {
  campaign_tag_id: string
  name: string
}

export interface ICampaignTag {
  campaign_tag_id?: string
  name: string
  is_hidden: boolean
  campaign_id?: string
}

export enum CampaignTemplateChannel {
  sms = 'SMS',
  email = 'Email',
}

export enum CampaignTriggerType {
  cancellation = 'Cancellation',
  confirmation = 'Confirmation',
  opt_out = 'Opt-out',
  opt_in = 'Opt-in',
  help = 'Help',
  reschedule = 'Reschedule',
}

export enum CampaignMessageStatus {
  sent = 'Sent',
  delivered = sent,
  failed = 'Failed',
  pending = 'Pending',
}

export enum CampaignType {
  Promotion = 'Promotional',
  Recall = 'Recall & recare',
  Reminder = 'Reminder',
  Celebration = 'Celebration',
  'Pat. edu' = 'Patient education',
  'Follow-up' = 'Follow-up',
  'Post-Tx' = 'Post-appt. review',
}

/** Email template builder canvas (behind the HTML body). Optional; APIs may omit it. */
export interface IEmailEditorCanvas {
  page_color?: string
  page_width?: string
  page_height?: string
  /** When set, drives --mt-email-header-band-bg for recall / thank-you strips, Elements header band, and Hero + text layout (solid fill). */
  header_band_color?: string
  /** When set, drives --mt-email-cta-btn-bg for Elements buttons and filled mock CTAs. */
  cta_button_bg_color?: string
  /** When set, drives --mt-email-cta-btn-bg-alt for Elements → Button 2 only. */
  cta_button_alt_bg_color?: string
  /** When set, drives --mt-email-cta-btn-fg for solid Elements buttons (primary) label color. */
  cta_button_text_color?: string
  /** When set, drives --mt-email-cta-btn-alt-fg for Elements → Button 2 solid label color. */
  cta_button_alt_text_color?: string
  /** When set, drives --mt-email-hero-copy-band-bg for Elements → Hero block (text under image). */
  hero_copy_band_color?: string
}

export interface ICampaignTemplate {
  campaign_template_id?: string
  clinic_id?: string
  channel: CampaignTemplateChannel
  name: string
  description?: string
  type: CampaignTemplateType
  text_content: string
  html_content: string
  url?: string
  email_editor_canvas?: IEmailEditorCanvas
  created_by: string
  is_hidden?: boolean
  date_time_created_at: string
  date_time_updated_at: string
}

/** Email template row shape (`campaign_template_email`). SMS uses `ICampaignTemplate`. */
export interface ICampaignTemplateEmail {
  campaign_template_id?: string
  clinic_id?: string
  name: string
  description?: string
  type: CampaignTemplateType
  text_content: string
  html_content: string
  url?: string
  email_editor_canvas?: IEmailEditorCanvas
  created_by: string
  is_hidden?: boolean
  date_time_created_at?: string
  date_time_updated_at?: string
}

export interface ICampaignTriggerKeyword {
  campaign_trigger_keyword_id?: string
  campaign_id: string
  name: string
  keyword: string
  campaign_template_id?: string
  subject?: string
  text_content: string
  html_content: string
  status: string
  is_hidden?: boolean
}

export interface ICampaignTriggerMessage {
  campaign_trigger_message_id?: string
  campaign_id: string
  name: string
  send_option: string
  send_on: number
  send_duration: string
  campaign_template_id?: string
  channel: string
  subject?: string
  text_content: string
  html_content: string
  status: string
  is_hidden?: boolean
}

export interface ICampaignRecipient {
  campaign_recipient_id?: string
  campaign_id: string
  contact_id: string
  is_hidden?: boolean
  contact?: IContact
}

export interface ICampaignMessage {
  campaign_message_id?: string
  contact_id: string
  campaign_id: string
  classification: 'Main' | 'Trigger'
  message_type: 'Incoming' | 'Outgoing'
  content: string
  cellphone?: string | null
  email?: string | null
  subject?: string
  message_sid?: string
  status: CampaignMessageStatus
  error_code?: string
  error_reason?: string
  resend_count: number
  schedule_date_time?: string | Date
  date_time_sent?: string | null
  date_time_updated_at?: string
  is_hidden?: boolean
  contact?: IContact
  campaign_name?: string
}

export interface ICampaignRequest {
  campaign_id?: string
  name: string
  type?: CampaignType
  clinic_id?: string
  url?: string
  description?: string
  campaign_template_id?: string | null
  subject?: string
  text_content?: string
  html_content?: string
  channel: string
  message_status: string
  campaign_status: string
  timezone?: string
  start_date?: string
  start_time?: string
  end_date?: string
  end_time?: string
  sending_option?: string
  schedule_date?: string | Date // for "Later" sending option
  schedule_time?: string // for "Later" sending option
  recurring_interval?: string // for "Recurring" sending option
  recurring_days?: string // for week interval
  recurring_duration?: number
  recurring_time?: string
  location_filter: string[]
  patient_status_filter: string[]
  age_range_filter: string[]
  appt_status_filter: string[]
  recall_type_filter: string[]
  celebration_type_filter: string[]
  created_by: string
  updated_by: string
  is_hidden?: boolean
  campaign_template?: ICampaignTemplate | null
  provider?: ProviderDetails
  campaign_recipient: ICampaignRecipient[]
  campaign_message: ICampaignMessage[]
  campaign_trigger_keyword: ICampaignTriggerKeyword[]
  campaign_trigger_message: ICampaignTriggerMessage[]
}

export interface ICampaignResponse extends ICampaignRequest {
  platform: string
  sent: number
  open: number
  clicks: number
  dayOfWeek?: number
  statusType: StatusTypes
  uploadTime: string
  uploadDate: string
}

export interface ISmsCampaign {
  phoneNumbers: string[]
  message: string
}

export interface IElementButton {
  /** Stable key for click handling (display text may change copy) */
  id: string
  text: string
  icon: string
  /** Shown as native tooltip (e.g. how this differs from similar elements). */
  hint?: string
  active?: boolean
  disabled?: boolean
}

/** Full-body HTML starters for Marketing → Email template editor (empty canvas). */
export interface IEmailStarterLayout {
  id: string
  title: string
  subtitle: string
  html: string
}

export interface LinkData {
  text: string
  href: string
}
